/**
 * Location to Timezone and Currency mappings
 * 位置到时区和货币的映射
 */

interface LocationData {
  timezone: string;
  currency: string;
  countryCode?: string;
}

// 城市到时区和货币的映射
export const LOCATION_MAPPINGS: Record<string, LocationData> = {
  // 中国
  '北京': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '上海': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '广州': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '深圳': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '成都': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '重庆': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '杭州': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  '西安': { timezone: 'Asia/Shanghai', currency: 'CNY', countryCode: 'CN' },
  
  // 香港、澳门、台湾
  '香港': { timezone: 'Asia/Hong_Kong', currency: 'HKD', countryCode: 'HK' },
  '澳门': { timezone: 'Asia/Macau', currency: 'MOP', countryCode: 'MO' },
  '台北': { timezone: 'Asia/Taipei', currency: 'TWD', countryCode: 'TW' },
  '高雄': { timezone: 'Asia/Taipei', currency: 'TWD', countryCode: 'TW' },
  
  // 日本
  '东京': { timezone: 'Asia/Tokyo', currency: 'JPY', countryCode: 'JP' },
  '大阪': { timezone: 'Asia/Tokyo', currency: 'JPY', countryCode: 'JP' },
  '京都': { timezone: 'Asia/Tokyo', currency: 'JPY', countryCode: 'JP' },
  '福冈': { timezone: 'Asia/Tokyo', currency: 'JPY', countryCode: 'JP' },
  '札幌': { timezone: 'Asia/Tokyo', currency: 'JPY', countryCode: 'JP' },
  
  // 韩国
  '首尔': { timezone: 'Asia/Seoul', currency: 'KRW', countryCode: 'KR' },
  '釜山': { timezone: 'Asia/Seoul', currency: 'KRW', countryCode: 'KR' },
  
  // 东南亚
  '曼谷': { timezone: 'Asia/Bangkok', currency: 'THB', countryCode: 'TH' },
  '新加坡': { timezone: 'Asia/Singapore', currency: 'SGD', countryCode: 'SG' },
  '吉隆坡': { timezone: 'Asia/Kuala_Lumpur', currency: 'MYR', countryCode: 'MY' },
  '巴厘岛': { timezone: 'Asia/Makassar', currency: 'IDR', countryCode: 'ID' },
  '河内': { timezone: 'Asia/Ho_Chi_Minh', currency: 'VND', countryCode: 'VN' },
  '胡志明市': { timezone: 'Asia/Ho_Chi_Minh', currency: 'VND', countryCode: 'VN' },
  
  // 欧洲
  '伦敦': { timezone: 'Europe/London', currency: 'GBP', countryCode: 'GB' },
  '巴黎': { timezone: 'Europe/Paris', currency: 'EUR', countryCode: 'FR' },
  '罗马': { timezone: 'Europe/Rome', currency: 'EUR', countryCode: 'IT' },
  '柏林': { timezone: 'Europe/Berlin', currency: 'EUR', countryCode: 'DE' },
  '阿姆斯特丹': { timezone: 'Europe/Amsterdam', currency: 'EUR', countryCode: 'NL' },
  '苏黎世': { timezone: 'Europe/Zurich', currency: 'CHF', countryCode: 'CH' },
  
  // 北美
  '纽约': { timezone: 'America/New_York', currency: 'USD', countryCode: 'US' },
  '洛杉矶': { timezone: 'America/Los_Angeles', currency: 'USD', countryCode: 'US' },
  '旧金山': { timezone: 'America/Los_Angeles', currency: 'USD', countryCode: 'US' },
  '芝加哥': { timezone: 'America/Chicago', currency: 'USD', countryCode: 'US' },
  '多伦多': { timezone: 'America/Toronto', currency: 'CAD', countryCode: 'CA' },
  '温哥华': { timezone: 'America/Vancouver', currency: 'CAD', countryCode: 'CA' },
  
  // 大洋洲
  '悉尼': { timezone: 'Australia/Sydney', currency: 'AUD', countryCode: 'AU' },
  '墨尔本': { timezone: 'Australia/Melbourne', currency: 'AUD', countryCode: 'AU' },
  '奥克兰': { timezone: 'Pacific/Auckland', currency: 'NZD', countryCode: 'NZ' },
  
  // 中东
  '迪拜': { timezone: 'Asia/Dubai', currency: 'AED', countryCode: 'AE' },
};

/**
 * 根据位置获取时区和货币信息
 */
export function getLocationData(location: string): LocationData {
  // 处理国家代码-城市格式（如 JP-Kyoto, US-NewYork）
  if (location.includes('-')) {
    const parts = location.split('-');
    const cityPart = parts[1]; // 取城市部分
    
    // 尝试匹配城市英文名
    const cityNameMap: Record<string, string> = {
      'Kyoto': '京都',
      'Tokyo': '东京',
      'Osaka': '大阪',
      'Fukuoka': '福冈',
      'Sapporo': '札幌',
      'Seoul': '首尔',
      'Busan': '釜山',
      'Bangkok': '曼谷',
      'Singapore': '新加坡',
      'KualaLumpur': '吉隆坡',
      'Paris': '巴黎',
      'London': '伦敦',
      'NewYork': '纽约',
      'HongKong': '香港',
      'Taipei': '台北',
    };
    
    const chineseName = cityNameMap[cityPart];
    if (chineseName && LOCATION_MAPPINGS[chineseName]) {
      return LOCATION_MAPPINGS[chineseName];
    }
  }
  
  // 先尝试精确匹配
  if (LOCATION_MAPPINGS[location]) {
    return LOCATION_MAPPINGS[location];
  }
  
  // 尝试模糊匹配（包含关系）
  for (const [key, data] of Object.entries(LOCATION_MAPPINGS)) {
    if (location.includes(key) || key.includes(location)) {
      return data;
    }
  }
  
  // 默认返回中国时区
  return {
    timezone: 'Asia/Shanghai',
    currency: 'CNY',
    countryCode: 'CN',
  };
}

/**
 * 获取天气 API 的城市名称（英文）
 */
export function getWeatherCityName(location: string): string {
  // 处理国家代码-城市格式（如 JP-Kyoto）
  if (location.includes('-')) {
    const parts = location.split('-');
    const cityPart = parts[1]; // 直接使用英文城市名
    
    // 城市名称标准化
    const normalizedMap: Record<string, string> = {
      'Kyoto': 'Kyoto',
      'Tokyo': 'Tokyo',
      'Osaka': 'Osaka',
      'Fukuoka': 'Fukuoka',
      'Sapporo': 'Sapporo',
      'Seoul': 'Seoul',
      'Busan': 'Busan',
      'Bangkok': 'Bangkok',
      'Singapore': 'Singapore',
      'KualaLumpur': 'Kuala Lumpur',
      'Paris': 'Paris',
      'London': 'London',
      'NewYork': 'New York',
      'HongKong': 'Hong Kong',
      'Taipei': 'Taipei',
    };
    
    return normalizedMap[cityPart] || cityPart;
  }
  
  const cityMap: Record<string, string> = {
    '北京': 'Beijing',
    '上海': 'Shanghai',
    '广州': 'Guangzhou',
    '深圳': 'Shenzhen',
    '成都': 'Chengdu',
    '重庆': 'Chongqing',
    '杭州': 'Hangzhou',
    '西安': 'Xi\'an',
    '香港': 'Hong Kong',
    '澳门': 'Macau',
    '台北': 'Taipei',
    '高雄': 'Kaohsiung',
    '东京': 'Tokyo',
    '大阪': 'Osaka',
    '京都': 'Kyoto',
    '福冈': 'Fukuoka',
    '札幌': 'Sapporo',
    '首尔': 'Seoul',
    '釜山': 'Busan',
    '曼谷': 'Bangkok',
    '新加坡': 'Singapore',
    '吉隆坡': 'Kuala Lumpur',
    '巴厘岛': 'Bali',
    '河内': 'Hanoi',
    '胡志明市': 'Ho Chi Minh City',
    '伦敦': 'London',
    '巴黎': 'Paris',
    '罗马': 'Rome',
    '柏林': 'Berlin',
    '阿姆斯特丹': 'Amsterdam',
    '苏黎世': 'Zurich',
    '纽约': 'New York',
    '洛杉矶': 'Los Angeles',
    '旧金山': 'San Francisco',
    '芝加哥': 'Chicago',
    '多伦多': 'Toronto',
    '温哥华': 'Vancouver',
    '悉尼': 'Sydney',
    '墨尔本': 'Melbourne',
    '奥克兰': 'Auckland',
    '迪拜': 'Dubai',
  };
  
  return cityMap[location] || location;
}
